import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../services/axios';

/**
 * Verifica se uma notificação já foi lida pelo usuário.
 *
 * O backend pode retornar o campo `data_hora_leitura` tanto como `null` quanto
 * como a string literal "null", então precisamos tratar os dois casos.
 */
const isRead = (n) => n?.data_hora_leitura !== 'null' && n?.data_hora_leitura !== null;

/**
 * Percorre a lista de notificações da caixa de entrada e coleta os IDs
 * daquelas que ainda não foram lidas.
 *
 * Usamos isso logo antes de marcar em lote, para saber exatamente quais
 * notificações precisam ser atualizadas na API.
 */
const extractUnreadIds = (inbox) => {
  if (!Array.isArray(inbox)) return [];
  const ids = [];
  for (const notificacao of inbox) {
    // Pulamos as que já foram lidas — não precisamos mexer nelas
    if (isRead(notificacao)) continue;
    const id = notificacao?._raw?.id;
    if (id) ids.push(id);
  }
  return ids;
};

/**
 * Normaliza o contador de não lidas que vem da API.
 *
 * A resposta pode vir como string, number ou até undefined. Aqui garantimos
 * que sempre trabalhamos com um número inteiro >= 0, evitando que NaN ou
 * negativos acabem aparecendo no badge da interface.
 */
const normalizeUnread = (raw) => {
  const maybe = raw?.unread;
  const n = parseInt(maybe ?? 0, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Hook responsável por gerenciar as notificações do tenant (inquilino/empresa).
 *
 * Ele cuida de três coisas principais:
 *  1. Buscar a caixa de entrada (inbox) com as notificações do usuário.
 *  2. Manter um contador de não lidas atualizado para exibir no badge.
 *  3. Fazer polling periódico, atualizando os dados sem intervenção do usuário.
 *
 * @param {object} options - Configurações opcionais do hook.
 * @param {boolean} [options.enablePolling=true]       - Liga/desliga o polling automático.
 * @param {number}  [options.pollingIntervalMs=180000] - Intervalo do polling em milissegundos (padrão: 3 minutos).
 * @param {object}  [options.inboxPayload={}]          - Filtros/parâmetros extras para a busca do inbox.
 *
 * @returns {{
 *   inbox: Array,
 *   unread: number,
 *   unreadIds: Array,
 *   badgeText: string,
 *   loading: boolean,
 *   refresh: Function,
 *   markReadBatch: Function,
 *   clearBadge: Function,
 * }}
 */
const useTenantNotifications = (options = {}) => {
  const {
    enablePolling = true,
    pollingIntervalMs = 180000, // 3 minutos por padrão
    inboxPayload = {},
  } = options;

  // Lista de notificações da caixa de entrada
  const [inbox, setInbox] = useState([]);
  // Quantidade de notificações não lidas (usado no badge)
  const [unread, setUnread] = useState(0);
  // Controle de carregamento para mostrar skeletons/spinners na UI
  const [loading, setLoading] = useState(false);

  // Guardamos o payload do inbox em uma ref para que a função `refresh`
  // sempre acesse a versão mais recente sem precisar ser recriada a cada
  // renderização — isso evita que o polling reinicie desnecessariamente.
  const inboxPayloadRef = useRef(inboxPayload);
  useEffect(() => {
    inboxPayloadRef.current = inboxPayload;
  }, [inboxPayload]);

  /**
   * Busca simultaneamente o contador de não lidas e a lista do inbox.
   *
   * Usamos `Promise.allSettled` para que uma falha em uma requisição não
   * impeça a outra de atualizar — se o contador falhar, o inbox ainda
   * é carregado e vice-versa.
   */
  const refresh = useCallback(async () => {
    setLoading(true);

    const [unreadResp, inboxResp] = await Promise.allSettled([
      axios.get('/api/tenantNotifications/', { dest: 'unreadCount' }),
      axios.get('/api/tenantNotifications/', { dest: 'inbox', dataPost: inboxPayloadRef.current || {} }),
    ]);

    // Atualiza o badge só se a requisição de contagem foi bem-sucedida
    if (unreadResp.status === 'fulfilled') {
      setUnread(normalizeUnread(unreadResp.value?.data));
    }

    // Atualiza o inbox só se a requisição correspondente foi bem-sucedida
    if (inboxResp.status === 'fulfilled') {
      const data = inboxResp.value?.data;
   
      setInbox(Array.isArray(data) ? data : []);
    }

    setLoading(false);
  }, []);

  // IDs das notificações ainda não lidas — recalcula apenas quando o inbox muda
  const unreadIds = useMemo(() => extractUnreadIds(inbox), [inbox]);

  // Texto do badge: mostra o número se houver não lidas, ou string vazia
  // para que o componente de badge saiba quando se esconder
  const badgeText = useMemo(() => (unread > 0 ? String(unread) : ''), [unread]);

  /**
   * Zera o contador localmente, sem fazer nenhuma chamada à API.
   * Útil para dar um feedback visual imediato ao usuário enquanto
   * a marcação como lida ainda está sendo processada.
   */
  const clearBadge = useCallback(() => {
    setUnread(0);
  }, []);

  /**
   * Marca um conjunto de notificações como lidas na API.
   *
   * Se nenhuma lista for fornecida, usa automaticamente os `unreadIds`
   * calculados a partir do inbox atual. Após a operação:
   *  1. Zera o badge imediatamente (feedback visual rápido).
   *  2. Recarrega o inbox para refletir o estado real do servidor.
   *
   * Erros da API são silenciados com `.catch(() => {})` para não
   * interromper o fluxo — o pior caso é o badge voltar a aparecer
   * no próximo refresh do polling.
   */
  const markReadBatch = useCallback(
    async (notificationIds) => {
      const ids = Array.isArray(notificationIds) ? notificationIds : unreadIds;

      // Nada a fazer se não há não lidas — apenas limpa o badge por precaução
      if (!ids || ids.length === 0) {
        clearBadge();
        return;
      }

      await axios
        .post('/api/tenantNotifications/', { dest: 'markReadBatch', dataPost: { notificationIds: ids } })
        .catch(() => {}); // falha silenciosa — o polling corrigirá no próximo ciclo

      clearBadge();
      await refresh();
    },
    [unreadIds, refresh, clearBadge]
  );

  /**
   * Efeito principal: dispara o primeiro carregamento e, se habilitado,
   * agenda o polling periódico.
   *
   * O cleanup do efeito cancela o intervalo quando o componente é
   * desmontado ou quando as opções de polling mudam, evitando memory leaks.
   */
  useEffect(() => {
    // Carrega imediatamente ao montar
    refresh();

    if (!enablePolling) return;
    const ms = Number(pollingIntervalMs);
    if (!Number.isFinite(ms) || ms <= 0) return;

    // Agenda o polling no intervalo configurado
    const intervalId = setInterval(() => {
      refresh();
    }, ms);

    // Limpa o intervalo ao desmontar ou quando as dependências mudarem
    return () => {
      try { clearInterval(intervalId); } catch {}
    };
  }, [refresh, enablePolling, pollingIntervalMs]);

  return {
    inbox,       // lista completa de notificações do inbox
    unread,      // quantidade de não lidas (número)
    unreadIds,   // IDs das notificações ainda não lidas
    badgeText,   // string do badge (vazia quando não há não lidas)
    loading,     // true enquanto uma requisição está em andamento
    refresh,     // força um recarregamento manual
    markReadBatch, // marca notificações como lidas em lote
    clearBadge,  // zera o badge localmente sem chamar a API
  };
};

export default useTenantNotifications;
